import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { db, storage, auth } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import { CLOUD_RUN_CONFIG } from '../config/cloudRun';
import { liveListRetryMs } from './liveListStore';
import { settledWithin } from '../utils/settledWithin';
import type { LiveCallBooking, LiveCallBookingStatus, LiveOfferingType } from '../types/liveCall';
import type { CallStatSample } from '../utils/callStats';
import { asMatchSession, type MatchSessionState } from '../utils/matchSession';

// ── Helpers ───────────────────────────────────────────────────────────────────

function addMinutes(iso: string, minutes: number): string {
  return new Date(new Date(iso).getTime() + minutes * 60_000).toISOString();
}

function normalizeBooking(data: Record<string, unknown>, id: string): LiveCallBooking {
  const toISO = (v: unknown): string => {
    if (v instanceof Timestamp) return v.toDate().toISOString();
    if (typeof v === 'string') return v;
    return new Date().toISOString();
  };
  return {
    ...(data as unknown as LiveCallBooking),
    id,
    createdAt: toISO(data.createdAt),
    updatedAt: toISO(data.updatedAt),
    scheduledStartAt: toISO(data.scheduledStartAt),
    windowOpenAt: toISO(data.windowOpenAt),
    windowCloseAt: toISO(data.windowCloseAt),
    ...(data.roomClosedAt ? { roomClosedAt: toISO(data.roomClosedAt) } : {}),
  };
}

// ── Create ────────────────────────────────────────────────────────────────────

export interface CreateBookingParams {
  offeringId: string;
  offeringType: LiveOfferingType;
  offeringTitle: string;
  coachId: string;
  playerId: string;
  coachName: string;
  playerName: string;
  coachAvatarUrl?: string;
  playerAvatarUrl?: string;
  scheduledStartAt: string;    // ISO timestamp
  durationMinutes: number;
  priceCents: number;
  membershipId?: string;
  creditsUsed?: number;
  playerMessage?: string;
  availabilitySlotId?: string; // lock the slot before writing the booking
}

export async function createLiveCallBooking(params: CreateBookingParams): Promise<LiveCallBooking> {
  const bookingRef = doc(collection(db, 'liveCallBookings'));
  const id = bookingRef.id;

  const windowOpenAt = addMinutes(params.scheduledStartAt, -30);
  const windowCloseAt = addMinutes(params.scheduledStartAt, params.durationMinutes + 30);

  const booking: Omit<LiveCallBooking, 'id'> = {
    offeringId: params.offeringId,
    offeringType: params.offeringType,
    offeringTitle: params.offeringTitle,
    coachId: params.coachId,
    playerId: params.playerId,
    coachName: params.coachName,
    playerName: params.playerName,
    coachAvatarUrl: params.coachAvatarUrl,
    playerAvatarUrl: params.playerAvatarUrl,
    scheduledStartAt: params.scheduledStartAt,
    durationMinutes: params.durationMinutes,
    windowOpenAt,
    windowCloseAt,
    status: params.priceCents === 0 ? 'confirmed' : 'pending_payment',
    roomId: `call-${id}`,
    priceCents: params.priceCents,
    membershipId: params.membershipId,
    creditsUsed: params.creditsUsed,
    playerMessage: params.playerMessage,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    transferScheduledAt: addMinutes(params.scheduledStartAt, params.durationMinutes + 24 * 60),
  };

  // Strip undefined values — Firestore rejects them
  const cleaned = Object.fromEntries(
    Object.entries(booking).filter(([, v]) => v !== undefined),
  );

  await setDoc(bookingRef, {
    ...cleaned,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  return { ...booking, id };
}

// ── Read ──────────────────────────────────────────────────────────────────────

export async function getLiveCallBooking(bookingId: string): Promise<LiveCallBooking | null> {
  const snap = await getDoc(doc(db, 'liveCallBookings', bookingId));
  if (!snap.exists()) return null;
  return normalizeBooking(snap.data(), snap.id);
}

export async function getUpcomingBookingsForPlayer(playerId: string): Promise<LiveCallBooking[]> {
  const cutoff = new Date().toISOString();
  const q = query(
    collection(db, 'liveCallBookings'),
    where('playerId', '==', playerId),
    where('windowCloseAt', '>=', cutoff),
    orderBy('windowCloseAt', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => normalizeBooking(d.data(), d.id))
    .filter((b) => b.status === 'confirmed' || b.status === 'pending_payment');
}

export async function getUpcomingBookingsForCoach(coachId: string): Promise<LiveCallBooking[]> {
  const cutoff = new Date().toISOString();
  const q = query(
    collection(db, 'liveCallBookings'),
    where('coachId', '==', coachId),
    where('windowCloseAt', '>=', cutoff),
    orderBy('windowCloseAt', 'asc'),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => normalizeBooking(d.data(), d.id))
    .filter((b) => b.status === 'confirmed' || b.status === 'pending_payment');
}

/** How many live lessons this coach and player have had together (completed ones). */
export async function countLiveLessonsTogether(coachId: string, playerId: string): Promise<number> {
  const q = query(
    collection(db, 'liveCallBookings'),
    where('coachId', '==', coachId),
    where('playerId', '==', playerId),
  );
  const snap = await getDocs(q);
  return snap.docs.filter((d) => d.data().status === 'completed').length;
}

/**
 * The player's Free Intro Call with a coach, once had (not cancelled): the newest, for the coach profile's
 * "Intro call done" and View (parity.md 1, native's state). Null when there is none.
 */
export async function findHadIntro(playerId: string, coachId: string): Promise<string | null> {
  const snap = await getDocs(query(
    collection(db, 'liveCallBookings'),
    where('playerId', '==', playerId),
    where('coachId', '==', coachId),
    where('offeringType', '==', 'free_intro_call'),
  ));
  const had = snap.docs.filter((d) => !String(d.data().status ?? '').startsWith('cancelled'))
    .sort((a, b) => String(b.data().scheduledStartAt ?? '').localeCompare(String(a.data().scheduledStartAt ?? '')));
  return had[0]?.id ?? null;
}

/** Live subscription to a single booking — used in the call room. */
export function subscribeToBooking(
  bookingId: string,
  onChange: (booking: LiveCallBooking | null) => void,
): () => void {
  return onSnapshot(doc(db, 'liveCallBookings', bookingId), (snap) => {
    onChange(snap.exists() ? normalizeBooking(snap.data(), snap.id) : null);
  }, () => onChange(null));
}

/** Every lesson between two people, whichever of them coaches: for the thread's finished line. */
export function subscribePairBookings(
  uidA: string,
  uidB: string,
  /** `ready` once both queries have answered. */
  onChange: (bookings: LiveCallBooking[], ready: boolean) => void,
): () => void {
  const col = collection(db, 'liveCallBookings');
  let one: LiveCallBooking[] | null = null;
  let two: LiveCallBooking[] | null = null;
  const emit = () => onChange([...(one ?? []), ...(two ?? [])], one !== null && two !== null);
  const offA = onSnapshot(query(col, where('playerId', '==', uidA), where('coachId', '==', uidB)),
    (snap) => { one = snap.docs.map((d) => normalizeBooking(d.data(), d.id)); emit(); }, () => { one = []; emit(); });
  const offB = onSnapshot(query(col, where('playerId', '==', uidB), where('coachId', '==', uidA)),
    (snap) => { two = snap.docs.map((d) => normalizeBooking(d.data(), d.id)); emit(); }, () => { two = []; emit(); });
  return () => { offA(); offB(); };
}

// ── Update ────────────────────────────────────────────────────────────────────

export async function updateBookingStatus(
  bookingId: string,
  status: LiveCallBookingStatus,
  extra?: Partial<LiveCallBooking>,
): Promise<void> {
  const ref = doc(db, 'liveCallBookings', bookingId);
  const payload: Record<string, unknown> = {
    status,
    updatedAt: serverTimestamp(),
    ...extra,
  };
  // Strip undefined
  Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
  await updateDoc(ref, payload);
}

/**
 * Player leaves after the coach did not come. The server settles the lesson
 * when the room closes: a coach who never joined missed it and the credit
 * returns (live lessons spec section 7).
 */
export async function reportCoachNoShow(bookingId: string): Promise<void> {
  await leaveLiveLesson(bookingId);
}

/** Confirm payment captured (called after Stripe payment intent succeeds). */
export async function confirmBookingPayment(
  bookingId: string,
  paymentIntentId: string,
): Promise<void> {
  const ref = doc(db, 'liveCallBookings', bookingId);
  await updateDoc(ref, {
    status: 'confirmed',
    paymentIntentId,
    paymentStatus: 'captured',
    updatedAt: serverTimestamp(),
  });
}

/**
 * Confirms the user's time zone (live lessons spec section 12): the device's
 * zone, "America/New_York" when the device gives none. Called at the first
 * booking.
 */
export async function confirmTimeZone(timeZone?: string): Promise<void> {
  const zone = timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone ||
    'America/New_York';
  await httpsCallable(functions, 'confirmLiveTimeZone')({ timeZone: zone });
}

/** The last leave sent, so a Rejoin's join is written after it (leaveSettled). */
let lastLeave: Promise<unknown> = Promise.resolve();

/** Leaving the room (a player's End). It never closes the room. */
export async function leaveLiveLesson(bookingId: string): Promise<void> {
  const sent = httpsCallable(functions, 'leaveLiveLesson')({ bookingId });
  lastLeave = sent;
  await sent;
}

/**
 * Once the last leave has landed, or after 2 s: the join waits on it, so a
 * Rejoin straight after Leave is not stamped before its leave.
 */
export function leaveSettled(): Promise<void> {
  return settledWithin(lastLeave, 2000);
}

/**
 * Record that the caller joined the room, and that they are still in it:
 * the call screen calls this about once a minute. The server writes the
 * times (joinLiveLesson); clients cannot. The role is kept for callers; the
 * server works it out from the signed-in user.
 */
export async function recordJoin(bookingId: string, _role: 'coach' | 'player',
  media?: { cameraOn?: boolean; micOn?: boolean; sharing?: boolean }): Promise<void> {
  await httpsCallable(functions, 'joinLiveLesson')({ bookingId, ...media });
}

/**
 * The lesson's chapters (decision 101), written whole: the coach's, while it
 * is booked and not finished (firestore.rules; docs/booking-contracts.md 8).
 */
export async function saveLiveChapters(bookingId: string, liveChapters: import('../types/liveCall').LiveChapter[]): Promise<void> {
  await updateDoc(doc(db, 'liveCallBookings', bookingId), { liveChapters, updatedAt: serverTimestamp() });
}

/** Update coach's pre-call prep notes and/or segment annotations on a booking. */
export async function updateBookingPrep(
  bookingId: string,
  prepNotes: string,
  prepSegments?: import('../types/liveCall').PrepSegment[],
): Promise<void> {
  const patch: Record<string, unknown> = { prepNotes, updatedAt: serverTimestamp() };
  if (prepSegments !== undefined) patch.prepSegments = prepSegments;
  await updateDoc(doc(db, 'liveCallBookings', bookingId), patch);
}

/** Persist post-call session notes on a booking (coach-authored, player-visible). */
export async function updateBookingSessionNotes(
  bookingId: string,
  sessionNotes: string,
): Promise<void> {
  await updateDoc(doc(db, 'liveCallBookings', bookingId), {
    sessionNotes,
    sessionNotesUpdatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/** Update the player video URL on a booking (set after player uploads). */
export async function updateBookingVideoRef(
  bookingId: string,
  playerVideoUrl: string,
  playerVideoStoragePath?: string,
): Promise<void> {
  const payload: Record<string, unknown> = {
    playerVideoUrl,
    updatedAt: serverTimestamp(),
  };
  if (playerVideoStoragePath) payload.playerVideoStoragePath = playerVideoStoragePath;
  await updateDoc(doc(db, 'liveCallBookings', bookingId), payload);
}

// ── Lessons list ──────────────────────────────────────────────────────────────

/** Statuses that belong on the Lessons screen. Unpaid and cancelled bookings never became a lesson. */
const LISTED_STATUSES: LiveCallBookingStatus[] = [
  'confirmed', 'completed', 'no_show_player', 'no_show_coach',
];

/** A player's cancel inside 12 hours is a player miss (spec 6, 7): it is
 *  listed, so the coach can count it and the player sees what it cost. */
const listed = (b: LiveCallBooking) =>
  LISTED_STATUSES.includes(b.status) || (b.status === 'cancelled_player' && !!b.lateCancel);

/**
 * Live calls for the Lessons screen, live. Filtered and sorted client-side so
 * the query needs only the single-field index Firestore creates by default.
 */
export function subscribeToLiveCallsForUser(
  role: 'player' | 'coach',
  uid: string,
  callback: (bookings: LiveCallBooking[]) => void,
): () => void {
  const q = query(
    collection(db, 'liveCallBookings'),
    where(role === 'coach' ? 'coachId' : 'playerId', '==', uid),
  );
  // A listener that errors is dead, and an empty list in its place read as
  // "No lessons yet" until a reload (after a room, say). The last list stays
  // and the listener starts again, backing off (liveListRetryMs).
  let unsub: (() => void) | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let attempt = 0;
  let stopped = false;
  const listen = () => {
    unsub = onSnapshot(q, (snap) => {
      attempt = 0;
      callback(snap.docs.map((d) => normalizeBooking(d.data(), d.id)).filter(listed));
    }, (err) => {
      console.error('[subscribeToLiveCallsForUser]', err);
      unsub = null;
      if (stopped) return;
      timer = setTimeout(listen, liveListRetryMs(attempt++));
    });
  };
  listen();
  return () => { stopped = true; if (timer) clearTimeout(timer); unsub?.(); };
}

const TERMINAL_STATUSES: LiveCallBookingStatus[] = [
  'completed', 'no_show_player', 'no_show_coach', 'cancelled_player', 'cancelled_coach',
];

// Calls open on this device. The card that hosts a call unmounts when its
// booking moves to Delivered, which would close the call, so an open call is
// never treated as ended: not when its window passes, and not when the stale
// sweep resolves it. It moves on the next re-partition after the call closes.
const openCalls = new Set<string>();

/** Marks a booking's call as open or closed on this device. */
export function setLiveCallOpen(bookingId: string, open: boolean): void {
  if (open) openCalls.add(bookingId);
  else openCalls.delete(bookingId);
}

/** Returns true once a call is in a terminal state or its room window has closed. */
export function hasLiveCallEnded(booking: LiveCallBooking, _now = Date.now()): boolean {
  if (openCalls.has(booking.id)) return false;
  if (TERMINAL_STATUSES.includes(booking.status)) return true;
  if (booking.status !== 'confirmed') return true;
  return !!booking.roomClosedAt;
}

// ── Player video ──────────────────────────────────────────────────────────────

/**
 * Upload a player's live call video through the same Cloud Run pipeline as
 * async lesson videos (web optimization + rally-clip dead time filtering).
 *
 * Creates a `pendingLessonUploads` doc so the CF trigger can start Cloud Run.
 * Returns immediately after the browser upload finishes with the raw download
 * URL; the CF writes the optimized URL back to the booking once Cloud Run is
 * done. Call `linkVideoUploadToBooking` after the booking is confirmed so the
 * CF knows which booking to update.
 */
export async function uploadLiveCallVideoForProcessing(
  playerId: string,
  coachId: string,
  offeringId: string,
  offeringTitle: string,
  file: File,
  onProgress: (percent: number) => void,
  /** The lesson it is for, when booked: the server then shows its one
   *  status on the booking's match from the first byte (decision 71). */
  bookingId?: string,
): Promise<{ uploadId: string; url: string; storagePath: string }> {
  const uploadRef = doc(collection(db, 'pendingLessonUploads'));
  const uploadId = uploadRef.id;
  const storagePath = `temp/lesson-uploads/${uploadId}/video.mp4`;

  await setDoc(uploadRef, {
    id: uploadId,
    playerId,
    coachId,
    offeringId,
    offeringTitle,
    priceCents: 0,
    kind: 'live_call_player_video',
    ...(bookingId && { liveCallBookingId: bookingId }),
    originalFileName: file.name,
    fileSize: file.size,
    tempStoragePath: storagePath,
    status: 'uploading',
    uploadProgress: 0,
    retryCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  const task = uploadBytesResumable(ref(storage, storagePath), file, {
    contentType: file.type || 'video/mp4',
  });
  // The document carries the progress too, so every screen reads one status (the pinned upload bar,
  // the Booked card, the rows), at most about once a second.
  let lastWrite = 0;
  await new Promise<void>((resolve, reject) => {
    task.on(
      'state_changed',
      (s) => {
        const pct = s.totalBytes ? (s.bytesTransferred / s.totalBytes) * 100 : 0;
        onProgress(pct);
        if (Date.now() - lastWrite > 900 && pct < 100) {
          lastWrite = Date.now();
          updateDoc(uploadRef, { uploadProgress: Math.round(pct), updatedAt: new Date().toISOString() }).catch(() => { /* the next one */ });
        }
      },
      reject,
      () => resolve(),
    );
  });

  // Transition to 'uploaded' — triggers the CF Firestore listener which starts Cloud Run
  await updateDoc(uploadRef, {
    status: 'uploaded',
    uploadProgress: 100,
    uploadedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  return { uploadId, url: await getDownloadURL(task.snapshot.ref), storagePath };
}

/**
 * Multi-file variant of uploadLiveCallVideoForProcessing.
 *
 * Uploads each file as a numbered part to temp/lesson-uploads/{uploadId}/parts/{i}.mp4,
 * then calls /stitch-player-videos on Cloud Run to concatenate them into
 * temp/lesson-uploads/{uploadId}/video.mp4 before marking the doc as 'uploaded'.
 *
 * Progress is reported 0-80% during uploads, 80-100% during stitching.
 * Returns the same shape as the single-file version.
 */
export async function uploadLiveCallVideosForProcessing(
  playerId: string,
  coachId: string,
  offeringId: string,
  offeringTitle: string,
  files: File[],
  onProgress: (percent: number) => void,
): Promise<{ uploadId: string; url: string; storagePath: string }> {
  const uploadRef = doc(collection(db, 'pendingLessonUploads'));
  const uploadId = uploadRef.id;
  const destPath = `temp/lesson-uploads/${uploadId}/video.mp4`;

  await setDoc(uploadRef, {
    id: uploadId,
    playerId,
    coachId,
    offeringId,
    offeringTitle,
    priceCents: 0,
    kind: 'live_call_player_video',
    originalFileName: files.map((f) => f.name).join(', '),
    fileSize: files.reduce((sum, f) => sum + f.size, 0),
    tempStoragePath: destPath,
    status: 'uploading',
    uploadProgress: 0,
    retryCount: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  let transferred = 0;
  const uploadWeight = 0.8;
  const partPaths: string[] = [];

  for (let i = 0; i < files.length; i++) {
    const partPath = `temp/lesson-uploads/${uploadId}/parts/${i}.mp4`;
    const storageRef = ref(storage, partPath);
    const fileSize = files[i].size;
    const bytesBeforeThis = transferred;

    await new Promise<void>((resolve, reject) => {
      const task = uploadBytesResumable(storageRef, files[i], { contentType: files[i].type || 'video/mp4' });
      task.on(
        'state_changed',
        (s) => {
          const filePct = s.totalBytes > 0 ? s.bytesTransferred / s.totalBytes : 0;
          const combined = (bytesBeforeThis + filePct * fileSize) / (totalBytes || 1);
          onProgress(combined * uploadWeight * 100);
        },
        reject,
        () => { transferred += fileSize; resolve(); },
      );
    });

    partPaths.push(partPath);
  }

  onProgress(uploadWeight * 100);

  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('User not authenticated');
  const idToken = await currentUser.getIdToken();

  const stitchRes = await fetch(`${CLOUD_RUN_CONFIG.VIDEO_PROCESSOR_URL}/stitch-player-videos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
    body: JSON.stringify({ uploadId, parts: partPaths }),
  });
  if (!stitchRes.ok) {
    const errData = await stitchRes.json().catch(() => ({ error: 'Unknown error' }));
    throw new Error(errData.error || `Stitch failed: ${stitchRes.status}`);
  }

  onProgress(100);

  await updateDoc(uploadRef, {
    status: 'uploaded',
    uploadProgress: 100,
    uploadedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });

  const url = await getDownloadURL(ref(storage, destPath));
  return { uploadId, url, storagePath: destPath };
}

/**
 * Wire the processed-video pipeline back to a specific booking.
 * Call this after `createLiveCallBookingIntentFn` returns a bookingId.
 * The CF trigger reads this field on the 'processed' status transition and
 * writes the optimized URL to liveCallBookings/{bookingId}/playerVideoUrl.
 */
export async function linkVideoUploadToBooking(
  uploadId: string,
  bookingId: string,
): Promise<void> {
  await updateDoc(doc(db, 'pendingLessonUploads', uploadId), {
    liveCallBookingId: bookingId,
    updatedAt: new Date().toISOString(),
  });
}

// ── Cloud Function callers ────────────────────────────────────────────────────

export interface CreateBookingIntentParams {
  offeringId: string;
  /** An open time, or with config/live's devAnyTime on, startAt instead. */
  slotId?: string;
  /** DEV ONLY: LIVE_DEV_ANY_TIME. Any future start on the slot grid (ISO). */
  startAt?: string;
  playerMessage?: string;
  saveCard?: boolean;
  paymentMethodId?: string;
  sessionId?: string;
  /** One action per key (booking-contracts.md 15): the quiet retry sends the same one on every try. */
  requestId?: string;
  /** Omitted for free_intro_call where no pre-call video is required. */
  playerVideoUrl?: string;
  /** Omitted for free_intro_call. Accepts users/{uid}/lesson-requests/ (legacy)
   *  or temp/lesson-uploads/ (Cloud Run processing pipeline). */
  playerVideoStoragePath?: string;
  /** IANA timezone of the player's browser at booking time — used for email formatting. */
  playerTimezone?: string;
  /** Live lessons v2.0 section 1: how a Live Video Lesson is used. The
   *  server ignores it for a Free Intro Call. */
  lessonMode?: 'match_review' | 'guidance_call';
  /** A Guidance Call's topics: College recruiting, Tournament planning,
   *  The mental game, What to work on next. Shown to the coach. */
  guidanceTopics?: string[];
}

export interface CreateBookingIntentResult {
  bookingId: string;
  status: 'confirmed' | 'pending_payment';
  paymentIntentId?: string;
  clientSecret?: string | null;
  alreadyConfirmed?: boolean;
}

const _createLiveCallBookingIntentFn = httpsCallable<
  CreateBookingIntentParams,
  CreateBookingIntentResult
>(functions, 'createLiveCallBookingIntent');

export async function createLiveCallBookingIntentFn(
  params: CreateBookingIntentParams,
): Promise<CreateBookingIntentResult> {
  // Until the booking screens ask (the mode step), a lesson booked with a
  // match is a Match Review and one without is a Guidance Call.
  const lessonMode = params.lessonMode ??
    (params.playerVideoUrl ? 'match_review' : 'guidance_call');
  const { data } = await _createLiveCallBookingIntentFn({ ...params, lessonMode });
  return data;
}

const _confirmLiveCallBookingFn = httpsCallable<
  { bookingId: string },
  { status: string }
>(functions, 'confirmLiveCallBooking');

export async function confirmLiveCallBookingFn(bookingId: string): Promise<void> {
  await _confirmLiveCallBookingFn({ bookingId });
}

const _releasePendingLiveCallBookingFn = httpsCallable<
  { bookingId: string },
  { released: boolean }
>(functions, 'releasePendingLiveCallBooking');

/** Frees the slot and deletes the booking when payment did not go through. */
export async function releasePendingLiveCallBookingFn(bookingId: string): Promise<void> {
  await _releasePendingLiveCallBookingFn({ bookingId });
}

const _cancelLiveCallBookingFn = httpsCallable<
  { bookingId: string },
  { status: string; refunded: boolean }
>(functions, 'cancelLiveCallBooking');

/**
 * Cancels a confirmed booking.
 * Player: full refund if > 24 h before start; forfeits otherwise.
 * Coach: always refunds the player.
 * Returns whether a payment refund was issued.
 */
export async function cancelBookingFn(
  bookingId: string,
): Promise<{ status: string; refunded: boolean }> {
  const { data } = await _cancelLiveCallBookingFn({ bookingId });
  return data;
}

const _generateCallNotesFn = httpsCallable<
  { bookingId: string; rawNotes?: string },
  { notes: string }
>(functions, 'generateCallNotes');

export async function generateCallNotesFn(
  bookingId: string,
  rawNotes?: string,
): Promise<string> {
  const { data } = await _generateCallNotesFn({ bookingId, rawNotes });
  return data.notes;
}

const _generateProgressReportFn = httpsCallable<
  { playerId: string; coachId: string; emailToPlayer?: boolean },
  { report: string; sentTo?: string }
>(functions, 'generateProgressReport');

export async function generateProgressReportFn(
  playerId: string,
  coachId: string,
  emailToPlayer = false,
): Promise<{ report: string; sentTo?: string }> {
  const { data } = await _generateProgressReportFn({ playerId, coachId, emailToPlayer });
  return data;
}

/** Verify invite token — returns the booking if the token matches any invite. */
export async function getBookingByInviteToken(
  bookingId: string,
  token: string,
): Promise<LiveCallBooking | null> {
  const invitesQ = query(
    collection(db, 'callInvites'),
    where('bookingId', '==', bookingId),
    where('token', '==', token),
  );
  const inviteSnap = await getDocs(invitesQ);
  if (inviteSnap.empty) return null;
  return getLiveCallBooking(bookingId);
}

/**
 * The call's 10 s sample, on UI/UX's liveCallStats contract (booking-contracts.md 13):
 * set at its id, one per side per slot (a second for the same slot is refused), at the server's time.
 */
export async function saveCallStat(id: string, data: CallStatSample): Promise<void> {
  await setDoc(doc(db, 'liveCallStats', id), { ...data, at: serverTimestamp() });
}

/** The match's session state, read by a peer as it joins (decision 130). Null when there is none, or it cannot be read. */
export async function readMatchSession(bookingId: string, sessionId: string): Promise<MatchSessionState | null> {
  const snap = await getDoc(doc(db, 'liveCallBookings', bookingId, 'sessions', sessionId));
  return snap.exists() ? asMatchSession(snap.data().match) : null;
}

/** The coach's player writes the match's state on the session (contract: docs/specs/match-session-state.md). */
export async function saveMatchSession(bookingId: string, sessionId: string, match: MatchSessionState): Promise<void> {
  await updateDoc(doc(db, 'liveCallBookings', bookingId, 'sessions', sessionId), { match });
}

/** The coach closed the match. */
export async function closeMatchSession(bookingId: string, sessionId: string, by: string): Promise<void> {
  await updateDoc(doc(db, 'liveCallBookings', bookingId, 'sessions', sessionId), { 'match.open': false, 'match.at': new Date().toISOString(), 'match.by': by });
}
